import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { formatDateFR } from "../utils/format";

function getClass(score) {
  if (score >= 90) return "excellent";
  if (score >= 80) return "bon";
  if (score >= 65) return "moyen";
  if (score >= 50) return "insuffisant";
  return "critique";
}

export default function VisitList() {
  const [visits, setVisits] = useState([]);
  const currentUser = api.getCurrentUser();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "director";
  const isSuperAdmin = currentUser?.role === "admin" && currentUser?.is_super_admin === true;
  const canValidate = currentUser?.role === "director" || isSuperAdmin;
  const isDirector = currentUser?.role === "director";
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => { api.getVisits().then(setVisits); }, []);

  const filtered = visits.filter(v => {
    const matchSearch = v.store_name.toLowerCase().includes(search.toLowerCase()) ||
      (v.zone || "").toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || v.status === filter || (filter === "completed" && v.status === "validated");
    return matchSearch && matchFilter;
  });

  async function handleValidate(e, id) {
    e.stopPropagation();
    if (!window.confirm("Valider cette visite ? L inspecteur ne pourra plus la modifier.")) return;
    try {
      await api.validateVisit(id);
      setVisits(vs => vs.map(v => v.id === id ? { ...v, status: "validated" } : v));
    } catch (err) { alert(err.message); }
  }

  async function handleUnvalidate(e, id) {
    e.stopPropagation();
    if (!window.confirm("Annuler la validation de cette visite ?")) return;
    try {
      await api.unvalidateVisit(id);
      setVisits(vs => vs.map(v => v.id === id ? { ...v, status: "completed" } : v));
    } catch (err) { alert(err.message); }
  }

  async function handleDelete(e, id) {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette visite ?")) return;
    await api.deleteVisit(id);
    setVisits(v => v.filter(x => x.id !== id));
  }

  return (
    <div>
      <div className="page-header-row page-header">
        <div>
          <h1>Visites</h1>
          <p>{visits.length} visite{visits.length > 1 ? "s" : ""} au total</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("/visits/new")}>➕ Nouvelle visite</button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center" style={{ marginBottom: 20, flexWrap: "wrap" }}>
        <input
          placeholder="Rechercher un magasin, une zone..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 300 }}
        />
        <div className="flex gap-2">
          {["all", "in_progress", "completed"].map(f => (
            <button key={f} className={`btn btn-sm ${filter === f ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter(f)}>
              {f === "all" ? "Toutes" : f === "in_progress" ? "En cours" : "Terminees"}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">◈</div>
          <h3>Aucune visite</h3>
          <p>Commencez par créer une nouvelle visite.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate("/visits/new")}>
            ➕ Nouvelle visite
          </button>
        </div>
      ) : (
        <div className="visit-list">
          {filtered.map(v => (
            <div key={v.id} className="visit-card" onClick={() => navigate(`/visits/${v.id}/summary`)}>
              <div className={`visit-score ${getClass(v.total_score)}`}>{v.total_score}</div>
              <div className="visit-info">
                <div className="visit-name">{v.store_name}</div>
                <div className="visit-meta">
                  <span>{v.visit_date ? formatDateFR(v.visit_date) : "Date non définie"}</span>
                  {v.zone && <span>{v.zone}</span>}
                  <span>{v.inspector || "—"}</span>
                  {v.visit_type && <span>{v.visit_type}</span>}
                </div>
              </div>
              <div className="flex gap-2 items-center" style={{ flexWrap: "wrap" }}>
                <span style={{
                  background: v.status === "validated" ? "#dcfce7" : v.status === "completed" ? "#fef3c7" : "#f1f5f9",
                  color: v.status === "validated" ? "var(--green)" : v.status === "completed" ? "var(--orange)" : "var(--muted)",
                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap"
                }}>
                  {v.status === "validated" ? "✅ Validee" : v.status === "completed" ? "Terminee" : "En cours"}
                </span>
                {v.status !== "validated" && (isDirector || v.created_by_username === currentUser?.username) && (
                  <button className="btn btn-sm btn-outline" onClick={e => { e.stopPropagation(); navigate(`/visits/${v.id}/checklist`); }}>
                    📋
                  </button>
                )}
                {canValidate && (v.status === "completed" || v.status === "in_progress") && (
                  <button onClick={e => handleValidate(e, v.id)}
                    style={{ background: "#dcfce7", color: "var(--green)", border: "1.5px solid var(--green)", borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                    ✅ Valider
                  </button>
                )}
                {isDirector && v.status === "validated" && (
                  <button onClick={e => handleUnvalidate(e, v.id)}
                    style={{ background: "#fee2e2", color: "var(--red)", border: "1.5px solid var(--red)", borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    🔄
                  </button>
                )}
                {v.status !== "validated" && (isDirector || v.created_by_username === currentUser?.username) && (
                  <button className="btn btn-sm btn-danger btn-icon" onClick={e => handleDelete(e, v.id)}>✕</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
